// @steered AudibleSecurityContext 1.2 2026-08-17

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FullscreenExitOutlined, FullscreenOutlined } from '@ant-design/icons';
import { getAppetizeConfig } from '../appetize/appetizeConfig';
import {
  APPETIZE_HOST_URL,
  createAppetizeSession,
  type AppetizeSessionState,
} from '../appetize/appetizeManager';
import type { AppetizePlatform } from '../appetize/appetize.types';
import './BrowserPreview.css';

interface AppetizePreviewProps {
  platform: AppetizePlatform;
  overrideLabel: string;
  weblab?: string;
  tabColor?: string;
  tabName?: string;
  standalone?: boolean;
  onFullscreenChange?: (isFullscreen: boolean) => void;
}

const STATUS_TEXT: Record<AppetizeSessionState['status'], string> = {
  'loading-sdk': 'Loading Appetize SDK…',
  starting: 'Starting device session…',
  queued: 'Waiting for an available device…',
  ready: 'Native app ready',
  disconnected: 'Device session disconnected',
  ended: 'Device session ended',
  failed: 'Native preview failed',
};

const AppetizePreview: React.FC<AppetizePreviewProps> = ({
  platform,
  overrideLabel,
  weblab,
  tabColor = '#3d8c6c',
  tabName = 'Native App Preview',
  standalone = false,
  onFullscreenChange,
}) => {
  const config = useMemo(() => getAppetizeConfig(platform), [platform]);
  const normalizedOverrideLabel = overrideLabel.trim();
  const [sessionState, setSessionState] = useState<AppetizeSessionState>({ status: 'loading-sdk' });
  const [retryKey, setRetryKey] = useState(0);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [width, setWidth] = useState(40);
  const [isResizing, setIsResizing] = useState(false);
  const [overrideCopied, setOverrideCopied] = useState(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(40);

  useEffect(() => {
    let controller: ReturnType<typeof createAppetizeSession> | null = null;
    // StrictMode cleans up its first effect before timers run, so deferring startup
    // prevents the replay from creating a duplicate SDK client or device session.
    const startId = window.setTimeout(() => {
      controller = createAppetizeSession({
        iframeId: config.buildId,
        config,
        onStateChange: setSessionState,
      });
    }, 0);

    const handleBeforeUnload = () => {
      void controller?.dispose();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.clearTimeout(startId);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      void controller?.dispose();
    };
  }, [config, retryKey]);

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (!isResizing) return;
      event.preventDefault();
      const containerWidth = document.querySelector('.chat-with-browser-container')?.clientWidth || window.innerWidth;
      const deltaPercent = ((startXRef.current - event.clientX) / containerWidth) * 100;
      setWidth(Math.min(65, Math.max(25, startWidthRef.current + deltaPercent)));
    };
    const handleMouseUp = () => setIsResizing(false);

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  const toggleFullscreen = () => {
    const nextValue = !isFullscreen;
    setIsFullscreen(nextValue);
    onFullscreenChange?.(nextValue);
  };

  const copyOverride = async () => {
    if (!normalizedOverrideLabel) return;
    await navigator.clipboard.writeText(`{${normalizedOverrideLabel}}`);
    setOverrideCopied(true);
    window.setTimeout(() => setOverrideCopied(false), 2000);
  };

  const retry = () => {
    setSessionState({ status: 'loading-sdk' });
    setRetryKey((value) => value + 1);
  };

  if (isMinimized) {
    return (
      <button
        className="browser-minimized appetize-minimized"
        onClick={() => setIsMinimized(false)}
        style={{ borderColor: tabColor }}
        type="button"
      >
        <span className="browser-minimized-icon" style={{ background: tabColor }}>📱</span>
        <span className="browser-minimized-info">
          <span className="browser-minimized-title">{tabName}</span>
          <span className="browser-minimized-url">{platform === 'ios' ? 'iOS' : 'Android'}</span>
        </span>
      </button>
    );
  }

  const isUnavailable = ['failed', 'disconnected', 'ended'].includes(sessionState.status);
  const statusText = sessionState.status === 'queued'
    ? `Queue position ${sessionState.position}`
    : STATUS_TEXT[sessionState.status];

  return (
    <div
      className={`browser-sidebar appetize-sidebar platform-${platform} ${standalone ? 'standalone' : ''} ${isFullscreen ? 'fullscreen' : ''}`}
      style={{ width: standalone || isFullscreen ? '100%' : `${width}%` }}
    >
      {!standalone && !isFullscreen && (
        <div
          className="browser-resize-handle"
          onMouseDown={(event) => {
            event.preventDefault();
            startXRef.current = event.clientX;
            startWidthRef.current = width;
            setIsResizing(true);
          }}
        />
      )}
      {isResizing && <div className="browser-resize-overlay" />}

      <div className="browser-sidebar-header" style={{ borderBottomColor: tabColor }}>
        <div className="browser-sidebar-title-wrapper">
          <div className="browser-sidebar-color-bar" style={{ backgroundColor: tabColor }} />
          <span className="browser-sidebar-title">📱 {tabName}</span>
          <span className={`appetize-status-badge ${sessionState.status}`}>{statusText}</span>
        </div>
        <div className="browser-header-actions">
          {!standalone && (
            <button
              className="browser-sidebar-fullscreen"
              onClick={toggleFullscreen}
              title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
              type="button"
            >
              {isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
            </button>
          )}
          {!standalone && !isFullscreen && (
            <button
              className="browser-sidebar-close"
              onClick={() => setIsMinimized(true)}
              title="Minimize"
              type="button"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      <div className={`browser-validation-panel appetize-setup-panel ${showInstructions ? 'expanded' : 'collapsed'}`}>
        <div className="appetize-setup-summary">
          <button
            aria-expanded={showInstructions}
            className="browser-validation-toggle appetize-guide-toggle"
            onClick={() => setShowInstructions((value) => !value)}
            type="button"
          >
            <span className="validation-toggle-icon" aria-hidden="true">{showInstructions ? '−' : '+'}</span>
            <span className="validation-toggle-text">{showInstructions ? 'Hide setup' : 'Setup guide'}</span>
          </button>
          <div className={`appetize-override-chip ${normalizedOverrideLabel ? '' : 'missing'}`}>
            <span>Override</span>
            <code title={normalizedOverrideLabel || 'No override selected'}>
              {normalizedOverrideLabel ? `{${normalizedOverrideLabel}}` : 'No override selected'}
            </code>
            <button
              aria-label="Copy native override"
              className={`browser-copy-btn ${overrideCopied ? 'copied' : ''}`}
              disabled={!normalizedOverrideLabel}
              onClick={copyOverride}
              type="button"
            >
              {overrideCopied ? '✓ Copied' : 'Copy'}
            </button>
          </div>
          <span
            className="appetize-account-badge"
            title="Use only an approved test account; do not enter personal customer credentials."
          >
            Test accounts only
          </span>
        </div>
        {showInstructions && (
          <div className="browser-validation-content appetize-guide-content">
            <div className="browser-validation-notes">
              <div className="validation-note">
                <strong>{platform === 'android' ? 'Android Debug Panel' : 'iOS Developer Settings'}</strong>
                {platform === 'android'
                  ? 'Open Debug Panel → G2S2 Configuration Version, then paste the override above. VPN/internal connectivity is required.'
                  : 'Open Developer Settings → G2S2 Configuration Version, then paste the override above. Native overrides are not URL parameters.'}
              </div>
              {weblab && (
                <div className="validation-note">
                  <strong>Weblab {weblab}</strong>
                  Apply the treatment through a Neo CID override for the approved test account, wait until active, then sign in or refresh.
                </div>
              )}
              <div className="validation-note appetize-security-note">
                <strong>Approved test accounts only</strong>
                Do not enter personal customer credentials until Appetize TPS and threat-model coverage is confirmed.
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="browser-sidebar-content appetize-content">
        <div className="appetize-device-frame">
          <iframe
            id={config.buildId}
            data-appetize-url={APPETIZE_HOST_URL}
            title={`${platform === 'ios' ? 'iOS' : 'Android'} Audible app preview`}
            className="appetize-iframe"
          />
        </div>
        {sessionState.status !== 'ready' && (
          <div className={`appetize-state-overlay ${isUnavailable ? 'unavailable' : ''}`} role="status">
            <span className="appetize-state-icon">{isUnavailable ? '⚠️' : '📱'}</span>
            <strong>{statusText}</strong>
            {sessionState.status === 'failed' && <span>{sessionState.message}</span>}
            {isUnavailable && (
              <button className="appetize-retry-button" onClick={retry} type="button">Retry</button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AppetizePreview;
